import React, { useId } from 'react';
import './Mascotte.css';

/**
 * Unified Titaton mascot component with multiple variants.
 *
 * @param {'accueil'|'hero'|'landing'|'notfound'|'coffre-empty'|'sceller'|'revelation'} variant
 * @param {number} size - width in px (height auto-scales)
 * @param {string} className - additional CSS class
 * @param {string} ariaLabel - accessibility label
 */
const Mascotte = ({ variant = 'accueil', size = 120, className = '', ariaLabel }) => {
  const uid = useId().replace(/:/g, '');
  const bodyId = `mb${uid}`;
  const goldId = `mg${uid}`;

  const SCENE_VARIANTS = ['premiers-pas', 'anniversaire', 'plage', 'dent', 'noel', 'creche'];
  const isScene = SCENE_VARIANTS.includes(variant);

  const label = ariaLabel || {
    accueil: 'Titaton mascotte',
    hero: 'Titaton vous accueille',
    landing: 'Titaton, la mascotte gardienne de souvenirs',
    notfound: 'Mascotte Titaton - page introuvable',
    'coffre-empty': 'Mascotte Titaton - attente',
    sceller: 'Mascotte Titaton - sceller',
    chargement: 'Mascotte Titaton - chargement',
    revelation: 'Titaton tout excite',
    'premiers-pas': 'Titaton fait ses premiers pas',
    anniversaire: 'Titaton fete son anniversaire',
    plage: 'Titaton a la plage',
    dent: 'Titaton montre sa premiere dent',
    noel: 'Titaton fete Noel',
    creche: 'Titaton va a la creche',
  }[variant];

  const isRevelation = variant === 'revelation';

  if (isScene) {
    return (
      <svg
        viewBox="0 0 220 240"
        width={size}
        height={size * (240 / 220)}
        className={`mascotte-svg ${className}`}
        aria-label={label}
        role="img"
      >
        <defs>
          <linearGradient id={bodyId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#F89A5E" />
            <stop offset="1" stopColor="#EA7637" />
          </linearGradient>
        </defs>
        <SceneVariant variant={variant} bodyId={bodyId} />
      </svg>
    );
  }

  const svgContent = (
    <svg
      viewBox="0 0 220 256"
      width={isRevelation ? size : size}
      height={isRevelation ? undefined : size * (256 / 220)}
      className={`mascotte-svg ${className}`}
      aria-label={label}
      role="img"
      style={isRevelation ? { width: size, height: 'auto' } : undefined}
    >
      <defs>
        <linearGradient id={bodyId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F89A5E" />
          <stop offset="1" stopColor="#EA7637" />
        </linearGradient>
        <radialGradient id={goldId} cx="40%" cy="35%" r="75%">
          <stop offset="0" stopColor="#F6D689" />
          <stop offset="55%" stopColor="#E6B24A" />
          <stop offset="100%" stopColor="#C9942F" />
        </radialGradient>
      </defs>

      {/* Shadow */}
      <ellipse cx="110" cy="219" rx="40" ry="7" fill="#C9A98E" opacity=".22" />
      {/* Feet */}
      <ellipse cx="86" cy="206" rx="13" ry="9" fill="#E06F37" />
      <ellipse cx="134" cy="206" rx="13" ry="9" fill="#E06F37" />
      {/* Crete du phenix (3 plumes) */}
      <path d="M110 26 C104 42 106 52 110 52 C114 52 116 42 110 26 Z" fill="#F6D689" className={variant === 'coffre-empty' ? 'mascotte-twk' : undefined} />
      <path d="M97 36 C92 48 95 57 101 56 C104 54 101 45 97 36 Z" fill="#EA7637" />
      <path d="M123 36 C128 48 125 57 119 56 C116 54 119 45 123 36 Z" fill="#EA7637" />
      {/* Body */}
      <path d="M110 48c44 0 70 30 70 78 0 52-30 86-70 86s-70-34-70-86c0-48 26-78 70-78Z" fill={`url(#${bodyId})`} />
      {/* Ears */}
      <path d="M58 86c-10-14-8-30 4-32 10-2 16 12 14 26Z" fill="#EA7637" />
      <path d="M162 86c10-14 8-30-4-32-10-2-16 12-14 26Z" fill="#EA7637" />
      {/* Cheeks */}
      <circle cx="74" cy="124" r="11" fill="#FBB089" opacity=".7" />
      <circle cx="146" cy="124" r="11" fill="#FBB089" opacity=".7" />

      {/* === Eyes === */}
      <VariantEyes variant={variant} />

      {/* === Mouth === */}
      <VariantMouth variant={variant} />

      {/* === Glow (before arms/coffre so it's behind) === */}
      <VariantGlow variant={variant} />

      {/* === Arms === */}
      <VariantArms variant={variant} />

      {/* === Coffre area === */}
      <VariantCoffre variant={variant} goldId={goldId} />

      {/* === Special elements (revelation heart/star/eclat) === */}
      {isRevelation && <RevelationExtras />}

      {/* === Timeline === */}
      <VariantTimeline variant={variant} />
    </svg>
  );

  if (isRevelation) {
    return (
      <div className="mascotte-revelation-wrap">
        {[
          { top: 2, left: 10, delay: '0s', s: 8 },
          { top: 0, right: 12, delay: '0.4s', s: 6 },
          { top: 30, left: 0, delay: '0.8s', s: 5 },
          { top: 28, right: 2, delay: '1.2s', s: 7 },
        ].map((sp, i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              top: sp.top, left: sp.left, right: sp.right,
              width: sp.s, height: sp.s,
              borderRadius: '50%',
              background: '#E6B24A',
              animation: `titaton-sparkle 1.6s ease-in-out ${sp.delay} infinite`,
              transformOrigin: 'center',
            }}
          />
        ))}
        {svgContent}
        <p className="mascotte-revelation-text">
          Un souvenir refait surface...
        </p>
      </div>
    );
  }

  return svgContent;
};

/* ─── Sub-components for variant-specific parts ─── */

const VariantEyes = ({ variant }) => {
  // Arc eyes (sceller + revelation)
  if (variant === 'sceller' || variant === 'revelation') {
    return (
      <>
        <path d="M82 111 q8 -7 16 0" stroke="#2E2A5E" strokeWidth="3.6" fill="none" strokeLinecap="round" />
        <path d="M122 111 q8 -7 16 0" stroke="#2E2A5E" strokeWidth="3.6" fill="none" strokeLinecap="round" />
      </>
    );
  }

  // Chargement: smaller round eyes
  if (variant === 'chargement') {
    return (
      <>
        <circle cx="90" cy="111" r="9" fill="#2E2A5E" />
        <circle cx="130" cy="111" r="9" fill="#2E2A5E" />
        <circle cx="92.6" cy="108.6" r="2.8" fill="#fff" />
        <circle cx="132.6" cy="108.6" r="2.8" fill="#fff" />
        <rect className="mascotte-eyelid" x="81" y="102" width="18" height="18" rx="9" fill="#EA7637" />
        <rect className="mascotte-eyelid" x="121" y="102" width="18" height="18" rx="9" fill="#EA7637" />
      </>
    );
  }

  // Round eyes (all others) — coffre-empty has slightly bigger eyes
  const isCoffreEmpty = variant === 'coffre-empty';
  const r = isCoffreEmpty ? 11 : 10;
  const highlightR = isCoffreEmpty ? 4 : 3.2;
  const highlightCx1 = isCoffreEmpty ? 93.5 : 93;
  const highlightCx2 = isCoffreEmpty ? 133.5 : 133;
  const highlightCy = isCoffreEmpty ? 106.6 : 107.6;

  return (
    <>
      <circle cx="90" cy="110" r={r} fill="#2E2A5E" />
      <circle cx="130" cy="110" r={r} fill="#2E2A5E" />
      <circle cx={highlightCx1} cy={highlightCy} r={highlightR} fill="#fff" />
      <circle cx={highlightCx2} cy={highlightCy} r={highlightR} fill="#fff" />
      {/* Eyelids (blink) */}
      <rect className="mascotte-eyelid" x="79" y="101" width="22" height="22" rx="11" fill="#EA7637" />
      <rect className="mascotte-eyelid" x="119" y="101" width="22" height="22" rx="11" fill="#EA7637" />
    </>
  );
};

const VariantMouth = ({ variant }) => {
  if (variant === 'notfound') {
    return <path d="M102 135 q8 -5 16 0" stroke="#2E2A5E" strokeWidth="3.6" fill="none" strokeLinecap="round" />;
  }
  if (variant === 'sceller') {
    return <path d="M101 130 q9 6 18 0" stroke="#2E2A5E" strokeWidth="3.8" fill="none" strokeLinecap="round" />;
  }
  if (variant === 'revelation') {
    return <path d="M99 129 Q110 144 121 129" stroke="#2E2A5E" strokeWidth="4.2" fill="none" strokeLinecap="round" />;
  }
  if (variant === 'coffre-empty') {
    return <path d="M99 130 Q110 143 121 130" stroke="#2E2A5E" strokeWidth="4.2" fill="none" strokeLinecap="round" />;
  }
  if (variant === 'chargement') {
    return <path d="M103 131 q7 4 14 0" stroke="#2E2A5E" strokeWidth="3.6" fill="none" strokeLinecap="round" />;
  }
  // accueil, hero, landing
  return <path d="M98 129 Q110 142 122 129" stroke="#2E2A5E" strokeWidth="4" fill="none" strokeLinecap="round" />;
};

const VariantGlow = ({ variant }) => {
  if (variant === 'landing' || variant === 'sceller') {
    return <ellipse className="mascotte-glow" cx="110" cy="190" rx={variant === 'sceller' ? 40 : 38} ry={variant === 'sceller' ? 26 : 20} fill="#F6D689" />;
  }
  if (variant === 'notfound') {
    return <ellipse className="mascotte-glow" cx="110" cy="190" rx="38" ry="25" fill="#F6D689" />;
  }
  if (variant === 'coffre-empty') {
    return <ellipse className="mascotte-glow" cx="110" cy="186" rx="40" ry="26" fill="#F6D689" />;
  }
  if (variant === 'chargement') {
    return <ellipse className="mascotte-glow" cx="110" cy="186" rx="34" ry="26" fill="#F6D689" />;
  }
  return null;
};

const VariantArms = ({ variant }) => {
  // Drooping/hugging arms (notfound + sceller)
  if (variant === 'notfound' || variant === 'sceller') {
    return (
      <>
        <path d="M72 156 q-6 20 14 24" stroke="#EA7637" strokeWidth="12" fill="none" strokeLinecap="round" />
        <path d="M148 156 q6 20 -14 24" stroke="#EA7637" strokeWidth="12" fill="none" strokeLinecap="round" />
      </>
    );
  }
  // Raised arms (revelation)
  if (variant === 'revelation') {
    return (
      <>
        <path d="M76 150 q-12 -20 -4 -34" stroke="#EA7637" strokeWidth="12" fill="none" strokeLinecap="round" />
        <path d="M144 150 q12 -20 4 -34" stroke="#EA7637" strokeWidth="12" fill="none" strokeLinecap="round" />
      </>
    );
  }
  // Chargement: wraparound arms holding hourglass
  if (variant === 'chargement') {
    return <path d="M70 158c-2 22 14 38 40 38s42-16 40-38" stroke="#EA7637" strokeWidth="12" fill="none" strokeLinecap="round" />;
  }
  // Coffre-empty: wraparound arms holding photo
  if (variant === 'coffre-empty') {
    return <path d="M70 158c-2 22 14 38 40 38s42-16 40-38" stroke="#EA7637" strokeWidth="12" fill="none" strokeLinecap="round" />;
  }
  // Standard outstretched arms (accueil, hero, landing)
  return (
    <>
      <path d="M72 152 q-16 4 -22 22" stroke="#EA7637" strokeWidth="12" fill="none" strokeLinecap="round" />
      <path d="M148 152 q16 4 22 22" stroke="#EA7637" strokeWidth="12" fill="none" strokeLinecap="round" />
    </>
  );
};

const VariantCoffre = ({ variant, goldId }) => {
  // Revelation: no coffre
  if (variant === 'revelation') return null;

  // Chargement: hourglass + loading dots
  if (variant === 'chargement') {
    return (
      <>
        <g className="mascotte-hgf">
          <rect x="93" y="168" width="34" height="5" rx="2.5" fill="#B5822F" />
          <rect x="93" y="196" width="34" height="5" rx="2.5" fill="#B5822F" />
          <path d="M97 173 H123 L110 184.5 Z" fill="#FCEFC9" stroke="#E6B24A" strokeWidth="1.3" />
          <path d="M110 184.5 L123 196 H97 Z" fill="#FCEFC9" stroke="#E6B24A" strokeWidth="1.3" />
          <path d="M102.5 176 H117.5 L110 184.5 Z" fill="#E6B24A" />
          <path d="M101.5 196 H118.5 L110 189.5 Z" fill="#E6B24A" />
        </g>
        <circle className="mascotte-dot" cx="150" cy="96" r="3" fill="#9C8C7E" />
        <circle className="mascotte-dot mascotte-dot-b" cx="160" cy="96" r="3" fill="#9C8C7E" />
        <circle className="mascotte-dot mascotte-dot-c" cx="170" cy="96" r="3" fill="#9C8C7E" />
      </>
    );
  }

  // Coffre-empty: holding photo + open lid
  if (variant === 'coffre-empty') {
    return (
      <>
        {/* Photo in arms */}
        <g>
          <rect x="97" y="156" width="26" height="21" rx="2.5" fill="#fff" stroke="#E0C9A8" strokeWidth="1" />
          <rect x="99.5" y="158.5" width="21" height="16" rx="1.5" fill="#FBE9D2" />
          <circle cx="105" cy="164.5" r="3" fill="#F2A04B" />
          <path d="M99.5 171 L107 166 L113 170 L120.5 166 V174.5 H99.5 Z" fill="#9CC79A" />
        </g>
        {/* Twinkle star */}
        <path className="mascotte-twk" d="M132 156 l1 2.2 2.2 .4 -1.6 1.6 .4 2.3 -2-1.1 -2 1.1 .4-2.3 -1.6-1.6 2.2-.4Z" fill="#E6B24A" />
        {/* Coffre body */}
        <rect x="85" y="186" width="50" height="20" rx="5" fill={`url(#${goldId})`} stroke="#7A5A2E" strokeWidth="1.1" />
        <rect x="85" y="186" width="50" height="7" rx="3" fill="#D9A441" stroke="#7A5A2E" strokeWidth=".9" />
        <rect x="105" y="186" width="10" height="12" rx="2" fill="#FBE3A0" stroke="#7A5A2E" strokeWidth=".8" />
        {/* Open lid */}
        <rect x="82" y="176" width="56" height="12" rx="6" fill="#D9A441" stroke="#7A5A2E" strokeWidth="1.1" transform="rotate(-104 84 188)" />
      </>
    );
  }

  // Sceller: treasure star + closed coffre
  if (variant === 'sceller') {
    return (
      <>
        <path className="mascotte-twk" d="M110 166 l1.2 2.6 2.8 .4 -2 2 .5 2.8 -2.5-1.4 -2.5 1.4 .5-2.8 -2-2 2.8-.4Z" fill="#E6B24A" />
        <rect x="85" y="185" width="50" height="21" rx="5" fill={`url(#${goldId})`} stroke="#7A5A2E" strokeWidth="1.1" />
        <rect x="85" y="185" width="50" height="7" rx="3" fill="#D9A441" stroke="#7A5A2E" strokeWidth=".9" />
        <rect x="105" y="185" width="10" height="13" rx="2" fill="#FBE3A0" stroke="#7A5A2E" strokeWidth=".8" />
        <circle cx="110" cy="191" r="1.6" fill="#7A5A2E" />
        {/* Closed lid */}
        <rect x="82" y="175" width="56" height="13" rx="6" fill="#D9A441" stroke="#7A5A2E" strokeWidth="1.1" />
      </>
    );
  }

  // NotFound: closed coffre + padlock
  if (variant === 'notfound') {
    return (
      <>
        <rect x="85" y="185" width="50" height="21" rx="5" fill={`url(#${goldId})`} stroke="#7A5A2E" strokeWidth="1.1" />
        <rect x="85" y="185" width="50" height="7" rx="3" fill="#D9A441" stroke="#7A5A2E" strokeWidth=".9" />
        {/* Closed lid */}
        <rect x="82" y="175" width="56" height="13" rx="6" fill="#D9A441" stroke="#7A5A2E" strokeWidth="1.1" />
        {/* Padlock */}
        <path d="M106 189 v-2.6 a4 4 0 0 1 8 0 v2.6" fill="none" stroke="#2C5E9E" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="104" y="189" width="12" height="9" rx="2" fill="#3D7BC7" />
        <circle cx="110" cy="193" r="1.2" fill="#fff" />
      </>
    );
  }

  // Default (accueil, hero, landing): open coffre with empty cross
  return (
    <>
      {/* Empty coffre indicator (dashed box + cross) */}
      <g className="mascotte-twk" opacity=".8">
        <rect x="99" y="158" width="22" height="18" rx="3" fill="none" stroke="#C9942F" strokeWidth="1.4" strokeDasharray="3 3" />
        <line x1="110" y1="164" x2="110" y2="170" stroke="#C9942F" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="107" y1="167" x2="113" y2="167" stroke="#C9942F" strokeWidth="1.8" strokeLinecap="round" />
      </g>
      {/* Coffre body */}
      <rect x="85" y="185" width="50" height="21" rx="5" fill={`url(#${goldId})`} stroke="#7A5A2E" strokeWidth="1.1" />
      <rect x="92" y="183" width="36" height="10" rx="2" fill="#8A5F1E" opacity=".22" />
      <rect x="85" y="185" width="50" height="7" rx="3" fill="#D9A441" stroke="#7A5A2E" strokeWidth=".9" />
      <rect x="105" y="185" width="10" height="13" rx="2" fill="#FBE3A0" stroke="#7A5A2E" strokeWidth=".8" />
      <circle cx="110" cy="191" r="1.6" fill="#7A5A2E" />
      {/* Open lid */}
      <rect x="82" y="175" width="56" height="13" rx="6" fill="#D9A441" stroke="#7A5A2E" strokeWidth="1.1" transform="rotate(-100 84 187)" />
    </>
  );
};

const VariantTimeline = ({ variant }) => {
  if (variant === 'revelation') {
    // Standard timeline
    return (
      <>
        <line x1="44" y1="234" x2="176" y2="234" stroke="#4B4593" strokeWidth="2.2" strokeLinecap="round" />
        <circle cx="66" cy="234" r="2.4" fill="#9C97C9" />
        <circle cx="110" cy="234" r="4" fill="#3D7BC7" />
        <circle cx="142" cy="234" r="2.4" fill="#9C97C9" />
        <path d="M176 227 l1.4 3 3.1 .5 -2.3 2.2 .5 3.2 -2.7-1.5 -2.7 1.5 .5-3.2 -2.3-2.2 3.1-.5Z" fill="#BBAE97" opacity=".55" />
      </>
    );
  }

  if (variant === 'notfound') {
    // Slightly different dot positions
    return (
      <>
        <line x1="44" y1="234" x2="176" y2="234" stroke="#4B4593" strokeWidth="2.2" strokeLinecap="round" />
        <circle cx="86" cy="234" r="4" fill="#3D7BC7" />
        <circle cx="124" cy="234" r="2.4" fill="#9C97C9" />
        <circle cx="152" cy="234" r="2.4" fill="#9C97C9" />
        <path d="M176 227 l1.4 3 3.1 .5 -2.3 2.2 .5 3.2 -2.7-1.5 -2.7 1.5 .5-3.2 -2.3-2.2 3.1-.5Z" fill="#BBAE97" opacity=".6" />
      </>
    );
  }

  if (variant === 'coffre-empty') {
    // Has starlit ring + star
    return (
      <>
        <line x1="44" y1="234" x2="176" y2="234" stroke="#4B4593" strokeWidth="2.2" strokeLinecap="round" />
        <circle cx="66" cy="234" r="2.4" fill="#9C97C9" />
        <circle cx="110" cy="234" r="4" fill="#3D7BC7" />
        <circle cx="142" cy="234" r="2.4" fill="#9C97C9" />
        <circle cx="176" cy="234" r="9" fill="none" stroke="#E6B24A" strokeWidth="1.2" opacity=".5" />
        <path className="mascotte-starlit" d="M176 226 l1.6 3.4 3.6 .6 -2.6 2.5 .6 3.7 -3.2-1.8 -3.2 1.8 .6-3.7 -2.6-2.5 3.6-.6Z" fill="#E6B24A" />
      </>
    );
  }

  // Default timeline (accueil, hero, landing, sceller)
  return (
    <>
      <line x1="44" y1="234" x2="176" y2="234" stroke="#4B4593" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="66" cy="234" r="2.4" fill="#9C97C9" />
      <circle cx="110" cy="234" r="4" fill="#3D7BC7" />
      <circle cx="142" cy="234" r="2.4" fill="#9C97C9" />
      <path d="M176 227 l1.4 3 3.1 .5 -2.3 2.2 .5 3.2 -2.7-1.5 -2.7 1.5 .5-3.2 -2.3-2.2 3.1-.5Z" fill="#BBAE97" opacity=".55" />
    </>
  );
};

const RevelationExtras = () => (
  <>
    {/* Heart */}
    <path
      d="M150 70c-7-5-10-9-10-13a5 5 0 0 1 10-1.6 5 5 0 0 1 10 1.6c0 4-3 8-10 13Z"
      fill="#EA5A6B"
      className="mascotte-pop"
    />
    {/* Star */}
    <path
      d="M70 76 l1.1 2.4 2.5 .4 -1.8 1.8 .4 2.6 -2.2-1.2 -2.2 1.2 .4-2.6 -1.8-1.8 2.5-.4Z"
      fill="#E6B24A"
      className="mascotte-twk"
    />
    {/* Golden dot */}
    <circle cx="158" cy="112" r="2.2" fill="#E6B24A" className="mascotte-twk" />
  </>
);

/* ─── Scene variants: themed mascot illustrations ─── */

const MascotteBase = ({ bodyId, children }) => (
  <>
    {/* Shadow */}
    <ellipse cx="110" cy="212" rx="36" ry="6" fill="#C9A98E" opacity=".22" />
    {/* Feet */}
    <ellipse cx="90" cy="200" rx="12" ry="8" fill="#E06F37" />
    <ellipse cx="130" cy="200" rx="12" ry="8" fill="#E06F37" />
    {/* Crete du phenix (3 plumes) */}
    <path d="M110 26 C104 42 106 52 110 52 C114 52 116 42 110 26 Z" fill="#F6D689" />
    <path d="M97 36 C92 48 95 57 101 56 C104 54 101 45 97 36 Z" fill="#EA7637" />
    <path d="M123 36 C128 48 125 57 119 56 C116 54 119 45 123 36 Z" fill="#EA7637" />
    {/* Body */}
    <path d="M110 44c40 0 64 28 64 72 0 48-28 78-64 78s-64-30-64-78c0-44 24-72 64-72Z" fill={`url(#${bodyId})`} />
    {/* Ears */}
    <path d="M62 82c-9-13-7-27 3-29 9-2 15 11 13 24Z" fill="#EA7637" />
    <path d="M158 82c9-13 7-27-3-29-9-2-15 11-13 24Z" fill="#EA7637" />
    {/* Cheeks */}
    <circle cx="78" cy="118" r="10" fill="#FBB089" opacity=".65" />
    <circle cx="142" cy="118" r="10" fill="#FBB089" opacity=".65" />
    {children}
  </>
);

const SceneVariant = ({ variant, bodyId }) => {
  if (variant === 'premiers-pas') {
    return (
      <MascotteBase bodyId={bodyId}>
        {/* Override feet: one forward */}
        <ellipse cx="78" cy="200" rx="12" ry="8" fill="#E06F37" />
        <ellipse cx="138" cy="196" rx="12" ry="8" fill="#E06F37" />
        {/* Happy eyes */}
        <circle cx="94" cy="106" r="9" fill="#2E2A5E" />
        <circle cx="126" cy="106" r="9" fill="#2E2A5E" />
        <circle cx="97" cy="103.5" r="3" fill="#fff" />
        <circle cx="129" cy="103.5" r="3" fill="#fff" />
        {/* Big excited smile */}
        <path d="M98 124 Q110 138 122 124" stroke="#2E2A5E" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        {/* Arms out for balance */}
        <path d="M72 140 q-18 -8 -26 4" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M148 140 q18 -8 26 4" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        {/* Little footprint trail */}
        <ellipse cx="50" cy="212" rx="5" ry="3" fill="#E06F37" opacity=".3" />
        <ellipse cx="36" cy="214" rx="4" ry="2.5" fill="#E06F37" opacity=".2" />
        {/* Motion lines */}
        <line x1="148" y1="180" x2="158" y2="176" stroke="#EA7637" strokeWidth="2" strokeLinecap="round" opacity=".4" />
        <line x1="150" y1="188" x2="160" y2="186" stroke="#EA7637" strokeWidth="2" strokeLinecap="round" opacity=".3" />
      </MascotteBase>
    );
  }

  if (variant === 'anniversaire') {
    return (
      <MascotteBase bodyId={bodyId}>
        {/* Party hat */}
        <path d="M110 14 L90 52 L130 52 Z" fill="#E6B24A" />
        <circle cx="110" cy="12" r="4" fill="#EA5A6B" />
        <line x1="94" y1="44" x2="126" y2="44" stroke="#3D7BC7" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="97" y1="38" x2="123" y2="38" stroke="#EA5A6B" strokeWidth="1.5" strokeLinecap="round" />
        {/* Happy arc eyes */}
        <path d="M84 108 q8 -7 16 0" stroke="#2E2A5E" strokeWidth="3.2" fill="none" strokeLinecap="round" />
        <path d="M120 108 q8 -7 16 0" stroke="#2E2A5E" strokeWidth="3.2" fill="none" strokeLinecap="round" />
        {/* Excited open mouth */}
        <path d="M99 124 Q110 138 121 124" stroke="#2E2A5E" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        {/* Arms: holding gift */}
        <path d="M72 148 q-4 18 10 22" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M148 148 q4 18 -10 22" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        {/* Gift box */}
        <rect x="90" y="170" width="40" height="28" rx="4" fill="#EA5A6B" />
        <rect x="90" y="170" width="40" height="8" rx="3" fill="#D14B5C" />
        <line x1="110" y1="170" x2="110" y2="198" stroke="#E6B24A" strokeWidth="3" />
        <path d="M105 170 q5 -8 5 -8 q5 8 5 8" stroke="#E6B24A" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        {/* Confetti */}
        <rect x="60" y="60" width="5" height="5" rx="1" fill="#3D7BC7" opacity=".6" transform="rotate(20 62 62)" />
        <rect x="155" y="55" width="4" height="4" rx="1" fill="#EA5A6B" opacity=".6" transform="rotate(-15 157 57)" />
        <circle cx="50" cy="90" r="2.5" fill="#E6B24A" opacity=".5" />
        <circle cx="168" y="80" r="2" fill="#3D7BC7" opacity=".5" />
      </MascotteBase>
    );
  }

  if (variant === 'plage') {
    return (
      <MascotteBase bodyId={bodyId}>
        {/* Sunglasses */}
        <rect x="80" y="100" width="22" height="16" rx="8" fill="#2E2A5E" />
        <rect x="118" y="100" width="22" height="16" rx="8" fill="#2E2A5E" />
        <line x1="102" y1="108" x2="118" y2="108" stroke="#2E2A5E" strokeWidth="2.5" />
        {/* Glasses shine */}
        <path d="M85 103 l4 2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" opacity=".5" />
        <path d="M123 103 l4 2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" opacity=".5" />
        {/* Happy mouth */}
        <path d="M98 126 Q110 138 122 126" stroke="#2E2A5E" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        {/* Arms out relaxed */}
        <path d="M72 146 q-16 6 -20 20" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M148 146 q16 6 20 20" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        {/* Parasol */}
        <line x1="168" y1="24" x2="168" y2="170" stroke="#7A5A2E" strokeWidth="3" strokeLinecap="round" />
        <path d="M132 28 Q168 8 204 28 Q168 38 132 28Z" fill="#EA5A6B" />
        <path d="M150 28 Q168 18 186 28 Q168 32 150 28Z" fill="#D14B5C" opacity=".5" />
        {/* Sand */}
        <path d="M0 220 Q60 208 110 218 Q160 228 220 216 L220 240 L0 240Z" fill="#F5DEB3" opacity=".4" />
        {/* Shell */}
        <circle cx="46" cy="220" r="4" fill="#FBE3A0" opacity=".6" />
        {/* Sun rays (top left) */}
        <circle cx="28" cy="28" r="10" fill="#E6B24A" opacity=".3" />
        <circle cx="28" cy="28" r="6" fill="#E6B24A" opacity=".5" />
      </MascotteBase>
    );
  }

  if (variant === 'dent') {
    return (
      <MascotteBase bodyId={bodyId}>
        {/* Big round eyes looking down at tooth */}
        <circle cx="94" cy="106" r="9" fill="#2E2A5E" />
        <circle cx="126" cy="106" r="9" fill="#2E2A5E" />
        <circle cx="96" cy="103" r="3" fill="#fff" />
        <circle cx="128" cy="103" r="3" fill="#fff" />
        {/* BIG open smile showing tooth */}
        <path d="M96 122 Q110 142 124 122" stroke="#2E2A5E" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        {/* The famous tooth! */}
        <rect x="106" y="128" width="8" height="8" rx="2" fill="#fff" stroke="#ddd" strokeWidth=".8" />
        {/* Arms: pointing at mouth */}
        <path d="M72 142 q-8 12 0 22" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M148 142 q-4 10 -16 16" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        {/* Sparkles around tooth */}
        <path d="M92 118 l-3-3" stroke="#E6B24A" strokeWidth="2" strokeLinecap="round" />
        <path d="M130 116 l3-3" stroke="#E6B24A" strokeWidth="2" strokeLinecap="round" />
        <circle cx="136" cy="130" r="2" fill="#E6B24A" opacity=".6" />
        <circle cx="84" cy="132" r="1.5" fill="#E6B24A" opacity=".5" />
        {/* Tooth fairy star */}
        <path d="M156 74 l1.2 2.6 2.8 .4 -2 2 .5 2.8 -2.5-1.4-2.5 1.4 .5-2.8-2-2 2.8-.4Z" fill="#E6B24A" className="mascotte-twk" />
      </MascotteBase>
    );
  }

  if (variant === 'noel') {
    return (
      <MascotteBase bodyId={bodyId}>
        {/* Santa hat */}
        <path d="M72 62 Q110 10 148 62 Z" fill="#D32F2F" />
        <ellipse cx="110" cy="62" rx="42" ry="8" fill="#fff" />
        <circle cx="142" cy="22" r="7" fill="#fff" />
        {/* Happy arc eyes */}
        <path d="M84 108 q8 -7 16 0" stroke="#2E2A5E" strokeWidth="3.2" fill="none" strokeLinecap="round" />
        <path d="M120 108 q8 -7 16 0" stroke="#2E2A5E" strokeWidth="3.2" fill="none" strokeLinecap="round" />
        {/* Jolly smile */}
        <path d="M98 124 Q110 140 122 124" stroke="#2E2A5E" strokeWidth="3.8" fill="none" strokeLinecap="round" />
        {/* Arms waving */}
        <path d="M72 142 q-14 -10 -22 2" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M148 142 q14 -10 22 2" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        {/* Scarf */}
        <path d="M80 148 Q110 158 140 148" stroke="#2E8B57" strokeWidth="6" fill="none" strokeLinecap="round" />
        <path d="M130 148 q2 10 -4 18" stroke="#2E8B57" strokeWidth="5" fill="none" strokeLinecap="round" />
        {/* Snowflakes */}
        <circle cx="40" cy="50" r="2.5" fill="#fff" opacity=".6" />
        <circle cx="180" cy="40" r="2" fill="#fff" opacity=".5" />
        <circle cx="30" cy="120" r="1.8" fill="#fff" opacity=".4" />
        <circle cx="190" cy="100" r="2.2" fill="#fff" opacity=".5" />
        <circle cx="60" cy="170" r="1.5" fill="#fff" opacity=".3" />
        {/* Star */}
        <path d="M178 60 l1.4 3 3.1.5-2.3 2.2.5 3.2-2.7-1.5-2.7 1.5.5-3.2-2.3-2.2 3.1-.5Z" fill="#E6B24A" />
      </MascotteBase>
    );
  }

  if (variant === 'creche') {
    return (
      <MascotteBase bodyId={bodyId}>
        {/* Eyes: slightly nervous/excited */}
        <circle cx="94" cy="106" r="10" fill="#2E2A5E" />
        <circle cx="126" cy="106" r="10" fill="#2E2A5E" />
        <circle cx="97" cy="103" r="3.5" fill="#fff" />
        <circle cx="129" cy="103" r="3.5" fill="#fff" />
        {/* Brave smile */}
        <path d="M100 126 Q110 136 120 126" stroke="#2E2A5E" strokeWidth="3.5" fill="none" strokeLinecap="round" />
        {/* Arms: one waving */}
        <path d="M72 142 q-14 -14 -20 -4" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M148 146 q10 8 6 22" stroke="#EA7637" strokeWidth="10" fill="none" strokeLinecap="round" />
        {/* Backpack (behind body — draw straps over) */}
        <rect x="120" y="100" width="38" height="44" rx="8" fill="#3D7BC7" />
        <rect x="124" y="104" width="30" height="14" rx="4" fill="#2C5E9E" />
        {/* Strap */}
        <path d="M132 100 Q140 88 140 100" stroke="#2C5E9E" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* Backpack pocket */}
        <rect x="130" y="124" width="18" height="12" rx="3" fill="#2C5E9E" />
        <circle cx="139" cy="130" r="1.5" fill="#E6B24A" />
        {/* Little heart (courage!) */}
        <path d="M56 76c-4-3-6-5.5-6-8a3 3 0 0 1 6-1 3 3 0 0 1 6 1c0 2.5-2 5-6 8Z" fill="#EA5A6B" opacity=".7" />
      </MascotteBase>
    );
  }

  return null;
};

export default Mascotte;
